import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { Search, ArrowRight, Calendar, Tag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PublicBlogPage = ({ isPreview, onRead }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');

  const { data: blogs = [], isLoading } = useQuery({
    queryKey: ['publicBlogs'],
    queryFn: async () => {
      const res = await axiosClient.get('/public/blog');
      return Array.isArray(res) ? res : (res.data || []);
    }
  });

  const filteredBlogs = blogs.filter(b => {
    const matchesSearch = b.title.toLowerCase().includes(search.toLowerCase());
    const matchesCat = category === 'All' || b.category === category;
    return matchesSearch && matchesCat;
  });

  const categories = ['All', ...new Set(blogs.map(b => b.category).filter(Boolean))];

  return (
    <div className="min-h-screen bg-[#020617] pt-24 pb-20 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-16 space-y-4 animate-in fade-in slide-in-from-bottom-4">
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight">
            Security <span className="text-teal-500">Intelligence</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Latest analysis, threat research, and updates from the AIRS Unit.
          </p>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-12 justify-center animate-in fade-in slide-in-from-bottom-6 duration-700">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-3.5 text-slate-500 w-5 h-5" />
            <input 
              type="text" 
              placeholder="Search articles..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-700 bg-[#0f172a] text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-600 outline-none"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 hide-scrollbar">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all border ${
                  category === cat 
                    ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-500/20' 
                    : 'bg-[#0f172a] text-slate-400 border-slate-700 hover:border-slate-500 hover:text-white'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="text-center py-20 text-slate-500 animate-pulse">Loading Intelligence Feed...</div>
        ) : filteredBlogs.length === 0 ? (
          <div className="text-center py-20 text-slate-500">
             <p className="mb-2">No articles found matching your criteria.</p>
             {/* Hint for Admin */}
             <p className="text-xs text-slate-600">(Admin Note: Ensure posts are marked 'Published')</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBlogs.map((blog) => (
              <article 
                key={blog.id} 
                className="group bg-[#0f172a] rounded-2xl overflow-hidden border border-slate-800 hover:border-blue-500/50 transition-all duration-300 flex flex-col h-full cursor-pointer hover:shadow-2xl hover:shadow-blue-900/10 hover:-translate-y-1"
                onClick={() => isPreview ? onRead(blog.slug) : navigate(`/blog/${blog.slug}`)}
              >
                {/* Image */}
                <div className="h-48 overflow-hidden bg-slate-900 relative">
                  {blog.featured_image_url ? (
                    <img 
                      src={blog.featured_image_url} 
                      alt={blog.title} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 opacity-90 group-hover:opacity-100"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-700">
                      <Tag className="w-10 h-10 opacity-20" />
                    </div>
                  )}
                  <div className="absolute top-4 left-4 bg-blue-600/90 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full backdrop-blur-md shadow-lg">
                    {blog.category || 'Update'}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center text-xs text-slate-500 mb-3 space-x-3">
                    <span className="flex items-center"><Calendar className="w-3 h-3 mr-1"/> {new Date(blog.published_at || blog.created_at).toLocaleDateString()}</span>
                  </div>
                  
                  <h2 className="text-xl font-bold text-white mb-3 group-hover:text-teal-400 transition-colors line-clamp-2 leading-tight">
                    {blog.title}
                  </h2>
                  
                  <p className="text-slate-400 text-sm line-clamp-3 mb-6 flex-1 leading-relaxed">
                    {blog.excerpt || blog.subtitle || "Click to read the full analysis..."}
                  </p>
                  
                  <div className="flex items-center text-teal-500 font-bold text-sm group-hover:translate-x-2 transition-transform">
                    Read Analysis <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default PublicBlogPage;